import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, RefreshCw } from "lucide-react";
import { API, api, fmtErr, imgSrc, money } from "./api";
import { Modal, labelCls, inputCls, btnPrimary, btnGhost } from "./Modal";
import { UploadField } from "./UploadField";

const STATUSES = ["In Stock", "Out of Stock", "Made to Order"];
const EMPTY = {
    title: "", category_id: "", sub_id: "", mrp: "", sale_price: "", gst: "",
    delivery: "", description: "", specs: "", image_url: "", status: "In Stock",
};

export const ProductsTab = () => {
    const [cats, setCats] = useState([]);
    const [products, setProducts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        axios.get(`${API}/categories`).then((r) => setCats(r.data)).catch(() => {});
        axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => toast.error("Could not load products"));
    };
    useEffect(() => {
        load();
    }, []);

    const subsOf = useMemo(
        () => (cid) => cats.find((c) => c.id === cid)?.subs || [],
        [cats]
    );

    const openNew = () => setEditing({ ...EMPTY });
    const openEdit = (p) => setEditing({ ...p });
    const openDuplicate = (p) => setEditing({ ...p, id: undefined, title: `${p.title} (Copy)` });

    const save = async () => {
        if (!editing.title.trim()) return toast.error("Please type the product name");
        if (!editing.category_id) return toast.error("Please choose a category");
        setBusy(true);
        const payload = {
            ...editing,
            mrp: editing.mrp === "" ? null : Number(editing.mrp),
            sale_price: editing.sale_price === "" ? null : Number(editing.sale_price),
            gst: editing.gst === "" ? null : Number(editing.gst),
        };
        try {
            if (editing.id) {
                await api().put(`/admin/products/${editing.id}`, payload);
            } else {
                await api().post("/admin/products", payload);
            }
            toast.success("Saved! Product is live on your website.");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(fmtErr(e));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}" permanently?`)) return;
        try {
            await api().delete(`/admin/products/${p.id}`);
            toast.success("Product deleted");
            load();
        } catch (e) {
            toast.error(fmtErr(e));
        }
    };

    return (
        <div data-testid="products-tab">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-heading font-bold text-2xl text-navy tracking-tight">Your Products</h2>
                    <p className="text-sm text-muted mt-1">{products.length} product{products.length === 1 ? "" : "s"} live on the website.</p>
                </div>
                <div className="flex gap-2">
                    <button onClick={load} className={btnGhost} data-testid="refresh-products-button" aria-label="Refresh">
                        <RefreshCw size={17} />
                    </button>
                    <button onClick={openNew} className={btnPrimary} data-testid="add-product-button">
                        <Plus size={18} /> Add New Product
                    </button>
                </div>
            </div>

            {products.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-line" data-testid="products-empty">
                    <p className="text-lg font-semibold text-navy">No products yet</p>
                    <p className="text-sm text-muted mt-1">Click "Add New Product" above to put your first item on the website.</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left" data-testid="products-table">
                        <thead>
                            <tr className="border-b border-line text-xs uppercase tracking-wider text-muted">
                                <th className="py-3 pr-4">Photo</th>
                                <th className="py-3 pr-4">Name</th>
                                <th className="py-3 pr-4">Category</th>
                                <th className="py-3 pr-4">Price</th>
                                <th className="py-3 pr-4">Status</th>
                                <th className="py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map((p) => (
                                <tr key={p.id} className="border-b border-line" data-testid={`product-row-${p.id}`}>
                                    <td className="py-3 pr-4">
                                        {p.image_url ? (
                                            <img src={imgSrc(p.image_url)} alt={p.title} className="h-14 w-14 object-contain border border-line bg-white p-1" />
                                        ) : (
                                            <span className="text-xs text-muted">No photo</span>
                                        )}
                                    </td>
                                    <td className="py-3 pr-4 font-semibold text-navy">{p.title}</td>
                                    <td className="py-3 pr-4 text-sm text-muted">
                                        {p.category_name}{p.sub_name ? ` › ${p.sub_name}` : ""}
                                    </td>
                                    <td className="py-3 pr-4 text-sm">
                                        {p.sale_price ? money(p.sale_price) : p.mrp ? money(p.mrp) : <span className="text-muted">On request</span>}
                                    </td>
                                    <td className="py-3 pr-4">
                                        <span className={`text-xs font-semibold px-2.5 py-1 ${
                                            p.status === "In Stock" ? "bg-green-100 text-green-800"
                                            : p.status === "Out of Stock" ? "bg-red-100 text-red-700"
                                            : "bg-amber-100 text-amber-800"}`}>
                                            {p.status}
                                        </span>
                                    </td>
                                    <td className="py-3">
                                        <div className="flex gap-2">
                                            <button onClick={() => openEdit(p)} className="p-2.5 border border-line hover:border-gold hover:text-gold" title="Edit" data-testid={`edit-product-${p.id}`}>
                                                <Pencil size={15} />
                                            </button>
                                            <button onClick={() => openDuplicate(p)} className="p-2.5 border border-line hover:border-navy" title="Duplicate" data-testid={`duplicate-product-${p.id}`}>
                                                <Copy size={15} />
                                            </button>
                                            <button onClick={() => remove(p)} className="p-2.5 border border-line hover:border-red-500 hover:text-red-600" title="Delete" data-testid={`delete-product-${p.id}`}>
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && (
                <Modal title={editing.id ? "Edit Product" : "Add New Product"} onClose={() => setEditing(null)} wide>
                    <div className="grid md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                            <label className={labelCls}>Product Name *</label>
                            <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                placeholder="e.g. Saturn High Back Chair" className={inputCls} data-testid="product-title-input" />
                        </div>
                        <div>
                            <label className={labelCls}>Main Category *</label>
                            <select value={editing.category_id}
                                onChange={(e) => setEditing({ ...editing, category_id: e.target.value, sub_id: "" })}
                                className={inputCls} data-testid="product-category-select">
                                <option value="">— Choose a category —</option>
                                {cats.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Sub-Category</label>
                            <select value={editing.sub_id} onChange={(e) => setEditing({ ...editing, sub_id: e.target.value })}
                                className={inputCls} disabled={!editing.category_id} data-testid="product-subcategory-select">
                                <option value="">— None —</option>
                                {subsOf(editing.category_id).map((s) => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>MRP / Regular Price (₹)</label>
                            <input type="number" min="0" value={editing.mrp ?? ""} onChange={(e) => setEditing({ ...editing, mrp: e.target.value })}
                                placeholder="e.g. 12000" className={inputCls} data-testid="product-mrp-input" />
                        </div>
                        <div>
                            <label className={labelCls}>Selling Price (₹)</label>
                            <input type="number" min="0" value={editing.sale_price ?? ""} onChange={(e) => setEditing({ ...editing, sale_price: e.target.value })}
                                placeholder="e.g. 9500" className={inputCls} data-testid="product-sale-price-input" />
                        </div>
                        <div>
                            <label className={labelCls}>GST %</label>
                            <input type="number" min="0" max="100" value={editing.gst ?? ""} onChange={(e) => setEditing({ ...editing, gst: e.target.value })}
                                placeholder="e.g. 18" className={inputCls} data-testid="product-gst-input" />
                        </div>
                        <div>
                            <label className={labelCls}>Delivery / Shipping Details</label>
                            <input value={editing.delivery} onChange={(e) => setEditing({ ...editing, delivery: e.target.value })}
                                placeholder="e.g. Free delivery in Hyderabad" className={inputCls} data-testid="product-delivery-input" />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>Product Description</label>
                            <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                placeholder="Simple description customers will read" className={inputCls} data-testid="product-description-input" />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>Technical Specifications</label>
                            <textarea rows={3} value={editing.specs} onChange={(e) => setEditing({ ...editing, specs: e.target.value })}
                                placeholder="e.g. Mesh back, nylon base, hydraulic gas lift..." className={inputCls} data-testid="product-specs-input" />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>Product Photo</label>
                            <UploadField value={editing.image_url} onChange={(url) => setEditing({ ...editing, image_url: url })} testid="product-image" />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls}>Availability</label>
                            <div className="grid grid-cols-3 gap-2" data-testid="product-status-options">
                                {STATUSES.map((s) => (
                                    <button key={s} type="button" onClick={() => setEditing({ ...editing, status: s })}
                                        className={`py-3.5 px-2 text-sm font-semibold border transition-colors ${
                                            editing.status === s ? "bg-navy text-white border-navy" : "border-line hover:border-gold"}`}
                                        data-testid={`status-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-3 mt-8">
                        <button onClick={save} disabled={busy} className={btnPrimary} data-testid="save-product-button">
                            {busy ? "Saving..." : "Save Product"}
                        </button>
                        <button onClick={() => setEditing(null)} className={btnGhost} data-testid="cancel-product-button">Cancel</button>
                    </div>
                </Modal>
            )}
        </div>
    );
};
